/**
 * BigSplice Sheets Page - GPU-accelerated spreadsheet viewer
 *
 * Uses Pixi.js canvas rendering for high-performance virtual scrolling
 * with edge prefetch for smooth navigation through large datasets.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import { lambdaService, FileInfo } from '../../services/lambdaService';
import { lambdaEndpoints } from '../../aws-config';
import {
  sheetsService,
  SchemaColumn,
  SearchCriteria,
} from '../../services/sheetsService';
import { s3SheetsService } from '../../services/s3SheetsService';
import { TopNav } from '../ui/top-nav';
import {
  Sheet,
  Search,
  RefreshCw,
  Loader2,
  FileSpreadsheet,
  Columns,
  Rows,
  Clock,
  X,
  Download,
  Copy,
  ChevronDown,
  Zap,
  Database,
  TableProperties,
  Sparkles,
  Play,
  Save,
  Layers,
  Trash2,
  Pencil,
  Globe,
  PanelLeftClose,
  PanelLeft,
  Server,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Terminal,
  Cpu,
  Mic,
  Video,
  FileType2,
  Image,
} from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { CanvasGrid } from './CanvasGrid';
import {
  CELL_WIDTH,
  EDGE_PREFETCH_THRESHOLD,
  FETCH_DEBOUNCE_MS,
  MAX_FETCH_RETRIES,
  VIEWPORT_INITIAL_ROWS,
  PREFETCH_EXTRA_ROWS,
  PREFETCH_EXTRA_COLS,
  getChunkSize,
} from './types';
import { useSheetsDemoContext } from './SheetsDemoContext';

// Operator display names
const OPERATORS: Record<string, string> = {
  eq: 'equals',
  ne: 'not equals',
  gt: 'greater than',
  lt: 'less than',
  gte: 'greater or equal',
  lte: 'less or equal',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
};

export const SheetsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialFile = queryParams.get('file') || '';

  // Demo context — returns null on main site, populated on demo.bigsplicer.com
  const demoCtx = useSheetsDemoContext();

  // Theme - use global next-themes (controlled by top-nav toggle)
  const { theme: globalTheme } = useTheme();
  const canvasTheme: 'dark' | 'light' = globalTheme === 'light' ? 'light' : 'dark';

  // File selection state
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [fileSearchTerm, setFileSearchTerm] = useState('');
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);

  // Schema state
  const [schema, setSchema] = useState<SchemaColumn[]>([]);
  const [totalRows, setTotalRows] = useState(0);

  // Data state - in-memory Map cache (like gigasheets)
  // Stores fetched rows during session, cleared on search/file change
  const [dataCache, setDataCache] = useState<Map<number, Record<string, unknown>>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());

  // Filter state
  const [filterColumn, setFilterColumn] = useState('');
  const [filterOperator, setFilterOperator] = useState<string>('contains');
  const [filterValue, setFilterValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<SearchCriteria | null>(null);
  const [totalMatched, setTotalMatched] = useState(0);


  // Stats
  const [lastQueryTime, setLastQueryTime] = useState<number | null>(null);
  const [cacheHit, setCacheHit] = useState(false);

  // Dynamic chunk size based on column count (wide files need smaller chunks)
  const chunkSizeRef = useRef(3000);

  // Fetch tracking - like gigasheets, only allow ONE prefetch at a time
  const fetchingChunksRef = useRef<Set<number>>(new Set());
  const failedChunksRef = useRef<Map<number, number>>(new Map()); // chunk → retry count
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const liveSearchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const prefetchInProgressRef = useRef<boolean>(false);
  // Column windowing: track which columns have been fetched from lambda
  const loadedColumnsRef = useRef<Set<string>>(new Set());
  const colFetchInProgressRef = useRef<boolean>(false);
  // Guard against concurrent loadSchema calls (e.g. click + auto-select useEffect)
  const schemaLoadingRef = useRef<boolean>(false);
  // Lock timer after initial data load — prevents background prefetch and duplicate
  // handleFileSelect calls (from auto-select useEffect) from overwriting the timer.
  // Unlocked on file change, search, filter, and refresh.
  const timerLockedRef = useRef<boolean>(false);
  // Ref mirror of dataCache — used by handleScroll to avoid re-render loops.
  // (If handleScroll depended on the dataCache *state*, every fetch would recreate
  //  handleScroll → re-render CanvasGrid → fire onScroll → infinite loop.)
  const dataCacheRef = useRef<Map<number, Record<string, unknown>>>(new Map());

  const searchPollRef = useRef<NodeJS.Timeout | null>(null);
  const preSearchColumnsRef = useRef<Set<string> | null>(null);

  // Keep dataCacheRef in sync with state (avoids stale closure in handleScroll)
  useEffect(() => { dataCacheRef.current = dataCache; }, [dataCache]);

  // Folder expansion for multi-file folders
  const [folderChildren, setFolderChildren] = useState<Map<string, FileInfo[]>>(new Map());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());


  // DuckDB WASM local mode state
  const [dataMode, setDataMode] = useState<'cloud' | 'local'>('cloud');
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const dataModeRef = useRef<'cloud' | 'local'>('cloud');
  const downloadAbortRef = useRef<AbortController | null>(null);

  // AI Column state
  const [aiColumns, setAiColumns] = useState<Array<{ name: string; prompt: string; mode: 'ai' | 'web_search'; model?: string; afterColumn?: string }>>([]);
  const [hiddenAiColumns, setHiddenAiColumns] = useState<Set<string>>(new Set());
  const [aiCellData, setAiCellData] = useState<Map<string, string>>(new Map()); // "colName:rowIdx" → value
  const [aiCellLoading, setAiCellLoading] = useState<Set<string>>(new Set()); // "colName:rowIdx" currently loading
  const [showAiColumnDialog, setShowAiColumnDialog] = useState(false);
  const [newAiColName, setNewAiColName] = useState('');
  const [newAiColPrompt, setNewAiColPrompt] = useState('');
  const [newAiColMode, setNewAiColMode] = useState<'ai' | 'web_search'>('ai');
  const [newAiColModel, setNewAiColModel] = useState('amazon.nova-micro-v1:0');
  const [promptSuggestions, setPromptSuggestions] = useState<string[]>([]);
  const [promptSuggestionIdx, setPromptSuggestionIdx] = useState(0);
  const [promptBracketStart, setPromptBracketStart] = useState<number | null>(null);
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [columnSearchQuery, setColumnSearchQuery] = useState('');
  const [aiColRefSearch, setAiColRefSearch] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editingAiColumn, setEditingAiColumn] = useState<string | null>(null); // name of column being edited

  // Bring Your Own LLM state
  const [showBYOLSetup, setShowBYOLSetup] = useState(false);
  const [byolTailscaleKey, setByolTailscaleKey] = useState('');
  const [byolMachineAddr, setByolMachineAddr] = useState('');
  const [byolConfigured, setByolConfigured] = useState(false);
  const [byolTestStatus, setByolTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [byolTestMessage, setByolTestMessage] = useState('');
  const [byolSaving, setByolSaving] = useState(false);

  // Load BYOL config from SSM on mount
  useEffect(() => {
    (async () => {
      try {
        const headers = await lambdaService.getAuthHeaders();
        const resp = await fetch(lambdaEndpoints.aiColumnInference.replace('/ai-column', '/ai-column/byol-config'), {
          method: 'GET',
          headers,
        });
        if (resp.ok) {
          const data = await resp.json();
          const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
          if (parsed.configured) {
            setByolMachineAddr(parsed.machine_addr || '');
            setByolConfigured(true);
          }
        }
      } catch { /* ignore — BYOL just won't be available */ }
    })();
  }, []);

  // Model options for AI columns — verified working on account 940847017865
  const MODEL_OPTIONS = useMemo(() => [
    { group: 'OpenAI', models: [
      { id: 'openai.gpt-oss-120b-1:0', name: 'GPT-OSS 120B', tier: 'standard' },
      { id: 'openai.gpt-oss-20b-1:0', name: 'GPT-OSS 20B', tier: 'fast' },
    ]},
    { group: 'Meta Llama', models: [
      { id: 'us.meta.llama4-maverick-17b-instruct-v1:0', name: 'Llama 4 Maverick 17B', tier: 'fast' },
      { id: 'us.meta.llama4-scout-17b-instruct-v1:0', name: 'Llama 4 Scout 17B', tier: 'fast' },
      { id: 'us.meta.llama3-3-70b-instruct-v1:0', name: 'Llama 3.3 70B', tier: 'standard' },
    ]},
    { group: 'Amazon Nova', models: [
      { id: 'amazon.nova-pro-v1:0', name: 'Nova Pro', tier: 'standard' },
      { id: 'amazon.nova-lite-v1:0', name: 'Nova Lite', tier: 'fast' },
      { id: 'amazon.nova-micro-v1:0', name: 'Nova Micro', tier: 'default' },
    ]},
    { group: 'Mistral', models: [
      { id: 'mistral.mistral-large-3-675b-instruct', name: 'Mistral Large 3 675B', tier: 'premium' },
      { id: 'us.mistral.pixtral-large-2502-v1:0', name: 'Pixtral Large', tier: 'standard' },
      { id: 'mistral.ministral-3-8b-instruct', name: 'Ministral 8B', tier: 'fast' },
    ]},
    { group: 'DeepSeek', models: [
      { id: 'us.deepseek.r1-v1:0', name: 'DeepSeek R1', tier: 'premium' },
    ]},
    { group: 'Qwen', models: [
      { id: 'qwen.qwen3-32b-v1:0', name: 'Qwen3 32B', tier: 'standard' },
      { id: 'qwen.qwen3-coder-30b-a3b-v1:0', name: 'Qwen3 Coder 30B', tier: 'standard' },
    ]},
    ...(byolConfigured || byolMachineAddr ? [{ group: 'Your LLM (Ollama)', models: [
      { id: 'byol:default', name: byolMachineAddr ? `Local — ${byolMachineAddr}` : 'Local LLM', tier: 'local' },
    ]}] : []),
  ], [byolMachineAddr, byolConfigured]);

  // AI Layer state
  const [showSaveLayerDialog, setShowSaveLayerDialog] = useState(false);
  const [saveLayerName, setSaveLayerName] = useState('');
  const [activeLayerName, setActiveLayerName] = useState<string | null>(null);

  const [savedLayers, setSavedLayers] = useState<Array<{
    name: string;
    savedAt: string;
    columns: Array<{ name: string; prompt: string }>;
    cellData: Record<string, string>;
  }>>([]);

  // Load layers from S3 when file changes
  useEffect(() => {
    if (!selectedFile) {
      setSavedLayers([]);
      return;
    }
    const { bucket, key } = getS3Location(selectedFile);
    sheetsService.loadLayers(bucket, key)
      .then((res) => {
        if (res.success && res.layers) {
          setSavedLayers(res.layers);
        } else {
          setSavedLayers([]);
        }
      })
      .catch(() => setSavedLayers([]));
  }, [selectedFile]);

  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    row: number;
    col: number;
    value: unknown;
    x: number;
    y: number;
  } | null>(null);

  // React to demo context state changes (demo.bigsplicer.com only)
  useEffect(() => {
    if (!demoCtx) return;

    if (demoCtx.files) {
      setFiles(demoCtx.files as FileInfo[]);
      setIsLoadingFiles(false);
    }
    if (demoCtx.selectedFile) {
      setSelectedFile(demoCtx.selectedFile as FileInfo);
      if (demoCtx.schema) {
        setSchema(demoCtx.schema);
        setVisibleColumns(new Set(demoCtx.schema.map((c) => c.name)));
        if (demoCtx.schema.length > 0) {
          setFilterColumn(demoCtx.schema[0].name);
        }
      }
      if (demoCtx.totalRows) setTotalRows(demoCtx.totalRows);
      if (demoCtx.rows) {
        const cache = new Map<number, Record<string, unknown>>();
        demoCtx.rows.forEach((row, idx) => cache.set(idx, row));
        setDataCache(cache);
        setIsLoading(false);
      }
    }
    if (demoCtx.activeFilter) {
      setActiveFilter(demoCtx.activeFilter);
    }
    if (demoCtx.totalMatched !== undefined) {
      setTotalMatched(demoCtx.totalMatched);
    }
    if (demoCtx.lastQueryTime !== undefined) {
      setLastQueryTime(demoCtx.lastQueryTime);
    }
  }, [demoCtx]);

  // Load files on mount
  useEffect(() => {
    if (demoCtx?.active) return; // Skip real API calls in demo mode
    loadFiles();
  }, []);

  // Auto-select file from URL param (handles with or without .parquet extension)
  // Only runs once when files first load — not on every folderChildren change
  const autoSelectDoneRef = useRef(false);
  useEffect(() => {
    if (autoSelectDoneRef.current || !initialFile || files.length === 0) return;
    autoSelectDoneRef.current = true;

    const searchTerm = initialFile.replace('.parquet', '');
    // Strip timestamp prefix (13-digit-) for matching, e.g. "1771210013330-copy-table" → "copy-table"
    const strippedTerm = searchTerm.replace(/^\d{13}-/, '');
    const file = files.find(
      (f) => {
        const fn = f.fileName.replace('.parquet', '');
        const sk = f.s3Key?.replace('.parquet', '') || '';
        return fn === searchTerm || sk === searchTerm ||
          fn === strippedTerm || fn.includes(searchTerm) || searchTerm.includes(fn);
      }
    );
    if (file) {
      const children = folderChildren.get(file.fileName);
      if (!children || children.length === 0) {
        // No children — select directly (multipart or single-file)
        handleFileSelect(file);
      } else {
        // Has children — expand folder and auto-select first child
        setExpandedFolders(new Set([file.fileName]));
        handleFileSelect(children[0]);
      }
    }
  }, [initialFile, files, folderChildren]);

  const loadFiles = async () => {
    if (demoCtx?.active) return; // Demo mode — mock files provided via context
    try {
      setIsLoadingFiles(true);
      // Fetch files and folders in parallel (Dashboard uses both APIs)
      const [response, folderResponse] = await Promise.all([
        lambdaService.listFiles(),
        lambdaService.listFolders(),
      ]);
      if (response.success) {
        // Group files by their top-level folder (same logic as Dashboard).
        // S3 keys look like: users/{uid}/data/{folder_name}/{file_name}
        // We extract {folder_name} and group, keeping one representative
        // FileInfo per folder so the sidebar mirrors the Dashboard listing.
        const folderMap = new Map<string, { files: typeof response.files; totalSize: number }>();

        for (const f of response.files) {
          const key = f.s3Key || f.fileName;
          // Extract relative path after /data/
          let relativePath = '';
          if (key.includes('/data/')) {
            relativePath = key.substring(key.indexOf('/data/') + 6);
          } else {
            relativePath = key;
          }
          // Top-level folder name is the first segment
          const topFolder = relativePath.split('/')[0];
          if (!topFolder) continue;

          if (!folderMap.has(topFolder)) {
            folderMap.set(topFolder, { files: [], totalSize: 0 });
          }
          const entry = folderMap.get(topFolder)!;
          entry.files.push(f);
          entry.totalSize += f.fileSize || 0;
        }

        // Build one representative FileInfo per folder.
        // For multi-file non-multipart folders, store children for expansion.
        const folderEntries: typeof response.files = [];
        const childrenMap = new Map<string, FileInfo[]>();
        folderMap.forEach(({ files: folderFiles, totalSize }, folderName) => {
          const multipart = folderFiles.find((f) => f.isMultipart);
          const parquetFiles = folderFiles.filter(
            (f) => f.fileName.endsWith('.parquet') || f.s3Key?.endsWith('.parquet')
          );

          if (multipart) {
            // Multipart dataset — single entry, no expansion
            folderEntries.push({
              ...multipart,
              fileName: folderName,
              fileSize: totalSize || multipart.fileSize,
            });
          } else if (parquetFiles.length >= 1) {
            // Folder with parquet files — always show as expandable folder
            const representative = parquetFiles[0];
            folderEntries.push({
              ...representative,
              fileName: folderName,
              fileSize: totalSize || representative.fileSize,
            });
            childrenMap.set(folderName, parquetFiles);
          } else {
            // No parquet files (e.g. only xlsx pending conversion) — skip
          }
        });
        setFolderChildren(childrenMap);

        // Add empty folders from listFolders() that have no files
        // (Dashboard shows these via its separate listFolders() call)
        if (folderResponse.success) {
          for (const folder of folderResponse.folders) {
            // Top-level folders only (no slash) and not already in our map
            if (!folder.includes('/') && !folderMap.has(folder)) {
              folderEntries.push({
                fileName: folder,
                fileSize: 0,
                uploadDate: '',
                s3Key: `users/placeholder/data/${folder}/`,
              } as FileInfo);
            }
          }
        }

        setFiles(folderEntries);
      } else {
        toast.error(response.error || 'Failed to load files');
      }
    } catch (error) {
      toast.error('Error loading files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const getS3Location = (file: FileInfo): { bucket: string; key: string; isMultipart: boolean } => {
    const key = file.s3Key || file.fileName;
    return {
      bucket: 'bigsplice-us-east-1',
      key,
      isMultipart: file.isMultipart || false,
    };
  };

  const handleFileSelect = async (file: FileInfo) => {
    // Prevent auto-select useEffect from re-triggering after navigate() updates the URL
    autoSelectDoneRef.current = true;
    // Skip if already loading this file (prevents double-call from click + useEffect)
    if (schemaLoadingRef.current && selectedFile?.fileName === file.fileName) return;
    setSelectedFile(file);
    setDataCache(new Map()); // Clear cache on file change
    setSchema([]);
    setActiveFilter(null);
    setFilterColumn('');
    setFilterValue('');
    setTotalMatched(0);
    fetchingChunksRef.current.clear();
    failedChunksRef.current.clear();
    loadedColumnsRef.current = new Set();
    colFetchInProgressRef.current = false;
    stopSearch();


    // Clear AI column state on file switch
    setAiColumns([]);
    setAiCellData(new Map());
    setAiCellLoading(new Set());
    setActiveLayerName(null);

    // Reset local mode — close DuckDB if previously active
    if (dataModeRef.current === 'local') {
      import('../../services/duckdbService').then((m) => m.duckdbService.close());
    }
    if (downloadAbortRef.current) {
      downloadAbortRef.current.abort();
      downloadAbortRef.current = null;
    }
    dataModeRef.current = 'cloud';
    setDataMode('cloud');
    setDownloadProgress(null);

    // Update URL (strip .parquet extension for cleaner URLs)
    const cleanFileName = file.fileName.replace('.parquet', '');
    navigate(`/sheets?file=${encodeURIComponent(cleanFileName)}`, { replace: true });

    await loadSchema(file);
  };

  const loadSchema = async (file: FileInfo) => {
    // Prevent concurrent loadSchema calls for the same file
    // (e.g. click handler + auto-select useEffect both trigger handleFileSelect)
    if (schemaLoadingRef.current) return;
    schemaLoadingRef.current = true;

    const { bucket, key, isMultipart } = getS3Location(file);

    try {
      setIsLoading(true);
      const startTime = Date.now();

      const response = await sheetsService.getSchema(bucket, key, { isMultipart });

      if (response.success && response.schema) {
        setSchema(response.schema);
        setTotalRows(response.row_count || 0);

        // Set dynamic chunk size based on column count
        chunkSizeRef.current = getChunkSize(response.schema.length);

        // Default: show ALL columns
        const defaultVisible = new Set(response.schema.map((c) => c.name));
        setVisibleColumns(defaultVisible);

        // Set first column as default filter column
        if (response.schema.length > 0) {
          setFilterColumn(response.schema[0].name);
        }

        // Load initial data — viewport-first for wide files
        const allColNames = response.schema.map((c) => c.name);
        loadedColumnsRef.current = new Set();

        if (response.schema.length > 20) {
          // Wide file: load only viewport columns first for instant render
          const viewportColCount = Math.ceil((window.innerWidth || 1200) / CELL_WIDTH) + 2;
          const viewportCols = allColNames.slice(0, Math.min(viewportColCount, allColNames.length));

          // Phase 1: Fetch viewport slice (fast — ~10 cols × 100 rows)
          chunkSizeRef.current = getChunkSize(viewportCols.length);
          await fetchChunk(file, 0, null, response.schema, defaultVisible, viewportCols, VIEWPORT_INITIAL_ROWS);
          loadedColumnsRef.current = new Set(viewportCols);

          // Phase 2: Background prefetch — expand rows + columns
          const prefetchColCount = Math.min(viewportColCount + PREFETCH_EXTRA_COLS, allColNames.length);
          const prefetchCols = allColNames.slice(0, prefetchColCount);
          chunkSizeRef.current = getChunkSize(prefetchCols.length);
          fetchChunk(file, 0, null, response.schema, defaultVisible, prefetchCols, VIEWPORT_INITIAL_ROWS + PREFETCH_EXTRA_ROWS, true);
          prefetchCols.forEach((c) => loadedColumnsRef.current.add(c));
        } else {
          // Narrow file: load all columns as before
          chunkSizeRef.current = getChunkSize(response.schema.length);
          await fetchChunk(file, 0, null, response.schema, defaultVisible);
          loadedColumnsRef.current = new Set(allColNames);
        }

        // Start background download for DuckDB local mode
        // Skip for very small files (<1MB) — cloud mode is already fast enough
        // and the DuckDB WASM download (~5MB) adds unnecessary visual churn
        const fileSize = file.fileSize || response.file_size || 0;
        if (fileSize >= 1_000_000 && fileSize <= 250_000_000 && !file.isMultipart) {
          startBackgroundDownload(file);
        }
      } else {
        toast.error(response.error || 'Failed to load schema');
      }
    } catch (error) {
      toast.error('Error loading schema');
      console.error(error);
    } finally {
      setIsLoading(false);
      schemaLoadingRef.current = false;
    }
  };

  /** Download parquet file in background and switch to DuckDB WASM local mode */
  const startBackgroundDownload = async (file: FileInfo) => {
    try {
      const { bucket, key } = getS3Location(file);

      // Get presigned download URL from orchestrator
      const urlResponse = await sheetsService.getDownloadUrl(bucket, key);
      if (!urlResponse.success || !urlResponse.url) return;

      // Abort controller for cancellation on file switch
      const abortController = new AbortController();
      downloadAbortRef.current = abortController;

      setDownloadProgress(0);
      const response = await fetch(urlResponse.url, { signal: abortController.signal });
      const contentLength = Number(response.headers.get('Content-Length') || urlResponse.file_size || 0);
      const reader = response.body!.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (contentLength > 0) {
          setDownloadProgress(Math.round((received / contentLength) * 100));
        }
      }

      // Combine into single buffer
      const buffer = new Uint8Array(received);
      let offset = 0;
      for (const chunk of chunks) {
        buffer.set(chunk, offset);
        offset += chunk.length;
      }

      // Initialize DuckDB WASM and load the parquet file
      const { duckdbService } = await import('../../services/duckdbService');
      await duckdbService.init();
      await duckdbService.loadParquetBuffer(buffer.buffer, 'data');

      // Switch to local mode
      dataModeRef.current = 'local';
      setDataMode('local');
      setDownloadProgress(null);
      downloadAbortRef.current = null;

      toast.success('Local mode active');
    } catch (err: any) {
      if (err?.name === 'AbortError') return; // User switched files, ignore
      console.error('Background download failed, staying in cloud mode:', err);
      setDownloadProgress(null);
      downloadAbortRef.current = null;
      // Silently stay in cloud mode
    }
  };

  // Fetch a chunk of data and add to in-memory cache (like gigasheets)
  // Returns a Promise so callers can track completion
  const fetchChunk = useCallback(async (
    file: FileInfo,
    chunkIndex: number,
    filter: SearchCriteria | null,
    schemaOverride?: SchemaColumn[],
    columnsOverride?: Set<string>,
    columnSubset?: string[],     // Only request these columns from lambda
    limitOverride?: number,      // Override chunk size for this call
    silent?: boolean,            // Skip loading indicator + timer updates (for background prefetch)
  ): Promise<void> => {
    const chunkSize = limitOverride || chunkSizeRef.current;
    const chunkStart = chunkIndex * chunkSize;

    // Skip if already fetching this chunk
    if (fetchingChunksRef.current.has(chunkIndex)) return;

    // Skip if this chunk has failed too many times
    const retries = failedChunksRef.current.get(chunkIndex) || 0;
    if (retries >= MAX_FETCH_RETRIES) return;

    fetchingChunksRef.current.add(chunkIndex);

    const currentColumns = columnsOverride || visibleColumns;

    // === LOCAL MODE: query DuckDB WASM instead of Lambda ===
    if (dataModeRef.current === 'local') {
      try {
        const startTime = Date.now();
        const { duckdbService } = await import('../../services/duckdbService');
        const cols = Array.from(currentColumns);

        if (filter) {
          const result = await duckdbService.search('data', cols, filter, chunkSize, chunkStart);
          setDataCache((prev) => {
            const next = new Map(prev);
            result.data.forEach((row, idx) => {
              next.set(chunkStart + idx, row);
            });
            return next;
          });
          setTotalMatched(result.totalMatched ?? 0);
        } else {
          const rows = await duckdbService.getData('data', cols, chunkSize, chunkStart);
          setDataCache((prev) => {
            const next = new Map(prev);
            rows.forEach((row, idx) => {
              next.set(chunkStart + idx, row);
            });
            return next;
          });
        }

        setCacheHit(false);
        setLastQueryTime(Date.now() - startTime);
      } catch (error) {
        console.error('DuckDB local query error:', error);
      } finally {
        fetchingChunksRef.current.delete(chunkIndex);
        if (fetchingChunksRef.current.size === 0) {
          setIsLoading(false);
        }
      }
      return;
    }

    // === CLOUD MODE: use Lambda orchestrator ===
    const { bucket, key, isMultipart } = getS3Location(file);
    const currentSchema = schemaOverride || schema;

    // Set loading to trigger shimmer animation (skip for silent background prefetch)
    if (!silent) setIsLoading(true);

    try {
      const startTime = Date.now();

      // Build match-all query: prefer a TEXT column because "contains ''" on numeric
      // columns returns 0 matches in the Rust worker (it needs a string column).
      const textCol = currentSchema.find(
        (c) => c.data_type === 'Utf8' || c.data_type === 'LargeUtf8' || c.data_type === 'BYTE_ARRAY'
      )?.name;
      const matchAllCol = textCol || currentSchema[0]?.name;
      if (!matchAllCol) return;

      // Use S3 response orchestrator for data loading (bypasses 6MB API Gateway limit)
      // Use search() directly instead of getData() to avoid redundant schema fetch.
      // getData() internally calls getSchema() again just to find firstCol — but we already have it.
      const selectCols = columnSubset || Array.from(currentColumns);
      const response = await s3SheetsService.search(
        bucket,
        key,
        filter || { column: matchAllCol, operator: 'contains', value: '' },
        {
          select_columns: selectCols,
          limit: chunkSize,
          offset: chunkStart,
          isMultipart,
        }
      );

      if (response.success && response.data) {
        // Guard: if server returns empty data for a non-filtered chunk 0 query,
        // treat it as a failure to prevent infinite re-fetch loops.
        if (!filter && chunkIndex === 0 && response.data.length === 0 && (response as any).total_rows > 0) {
          console.warn('Match-all query returned 0 rows despite total_rows > 0');
          failedChunksRef.current.set(chunkIndex, MAX_FETCH_RETRIES);
        } else {
          // Clear retry count on success
          failedChunksRef.current.delete(chunkIndex);
        }

        // Add/merge rows into in-memory cache (merge supports column windowing)
        setDataCache((prev) => {
          const next = new Map(prev);
          response.data!.forEach((row, idx) => {
            const existing = next.get(chunkStart + idx);
            next.set(chunkStart + idx, existing
              ? { ...existing, ...row as Record<string, unknown> }
              : row as Record<string, unknown>);
          });
          return next;
        });

        // Update totalMatched when there's an active filter
        if (filter && response.total_matched_before_limit !== undefined) {
          setTotalMatched(response.total_matched_before_limit);
        }
        if (!silent && !timerLockedRef.current) {
          setCacheHit(response.cache_hit || false);
          setLastQueryTime(Date.now() - startTime);
          timerLockedRef.current = true;
        }
      } else {
        // Track failure
        failedChunksRef.current.set(chunkIndex, retries + 1);
      }
    } catch (error) {
      console.error('Error fetching chunk:', error);
      failedChunksRef.current.set(chunkIndex, retries + 1);
    } finally {
      fetchingChunksRef.current.delete(chunkIndex);
      // Only set loading false if no more chunks are being fetched
      if (!silent && fetchingChunksRef.current.size === 0) {
        setIsLoading(false);
      }
    }
  }, [schema, visibleColumns]);

  // Handle scroll event from CanvasGrid - aggressive prefetch for smooth scrolling.
  // IMPORTANT: Uses dataCacheRef (not dataCache state) to avoid re-render loops.
  // If we put dataCache in deps, every fetch recreates handleScroll → new onScroll
  // prop → CanvasGrid re-draws → calls onScroll → infinite loop.
  // Fetch specific columns for a row range and merge into cache (for column windowing)
  const fetchColumnData = useCallback(async (
    cols: string[],
    startRow: number,
    endRow: number,
  ) => {
    if (!selectedFile || cols.length === 0) return;
    const { bucket, key, isMultipart } = getS3Location(selectedFile);
    const currentSchema = schema;
    const limit = endRow - startRow;

    const textCol = currentSchema.find(
      (c) => c.data_type === 'Utf8' || c.data_type === 'LargeUtf8' || c.data_type === 'BYTE_ARRAY'
    )?.name;
    const matchAllCol = textCol || currentSchema[0]?.name;
    if (!matchAllCol) return;

    try {
      const response = await s3SheetsService.search(
        bucket,
        key,
        activeFilter || { column: matchAllCol, operator: 'contains', value: '' },
        { select_columns: cols, limit, offset: startRow, isMultipart }
      );

      if (response.success && response.data) {
        setDataCache((prev) => {
          const next = new Map(prev);
          response.data!.forEach((row, idx) => {
            const existing = next.get(startRow + idx);
            next.set(startRow + idx, existing
              ? { ...existing, ...row as Record<string, unknown> }
              : row as Record<string, unknown>);
          });
          return next;
        });
      }
    } catch (error) {
      console.error('Error fetching column data:', error);
    }
  }, [selectedFile, schema, activeFilter]);

  const handleScroll = useCallback((startRow: number, endRow: number, startCol: number, endCol: number) => {
    if (demoCtx?.active) return; // Demo mode — don't fetch real data
    if (!selectedFile) return;

    // Clear previous debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const maxRows = activeFilter ? totalMatched : totalRows;
      if (maxRows === 0) return;
      const cs = chunkSizeRef.current;
      const cache = dataCacheRef.current;

      // For wide files, use loaded columns to keep fetches fast
      const useColWindowing = schema.length > 20 && loadedColumnsRef.current.size > 0;
      const scrollColSubset = useColWindowing ? Array.from(loadedColumnsRef.current) : undefined;

      // Calculate visible range and chunks needed
      const startChunk = Math.floor(startRow / cs);
      const endChunk = Math.floor(endRow / cs);

      // Check if visible area has data
      let visibleNeedsData = false;
      for (let i = startRow; i < Math.min(endRow, maxRows); i++) {
        if (!cache.has(i)) {
          visibleNeedsData = true;
          break;
        }
      }

      // If visible area needs data, fetch it immediately
      // Use silent mode so scroll fetches don't overwrite the user's query timer
      if (visibleNeedsData && !fetchingChunksRef.current.has(startChunk)) {
        fetchChunk(selectedFile, startChunk, activeFilter, undefined, undefined, scrollColSubset, undefined, true);
      }

      // Also fetch the next visible chunk if we're spanning two chunks
      if (endChunk > startChunk && !cache.has(endChunk * cs) && !fetchingChunksRef.current.has(endChunk)) {
        fetchChunk(selectedFile, endChunk, activeFilter, undefined, undefined, scrollColSubset, undefined, true);
      }

      // Aggressive prefetch - don't wait for visible to complete
      if (prefetchInProgressRef.current) return;

      // Always prefetch ahead in the scroll direction
      // Check rows below visible area
      const belowStart = endRow;
      const belowEnd = Math.min(maxRows, endRow + EDGE_PREFETCH_THRESHOLD);
      let unloadedBelow = 0;
      for (let i = belowStart; i < belowEnd; i++) {
        if (!cache.has(i)) unloadedBelow++;
      }

      // Check rows above visible area
      const aboveStart = Math.max(0, startRow - EDGE_PREFETCH_THRESHOLD);
      const aboveEnd = startRow;
      let unloadedAbove = 0;
      for (let i = aboveStart; i < aboveEnd; i++) {
        if (!cache.has(i)) unloadedAbove++;
      }

      // More aggressive trigger - prefetch when ANY unloaded rows nearby
      if (unloadedBelow > 100) {
        // Scrolling down - prefetch next chunk(s)
        const nextChunk = endChunk + 1;
        if (nextChunk * cs < maxRows && !cache.has(nextChunk * cs) && !fetchingChunksRef.current.has(nextChunk)) {
          prefetchInProgressRef.current = true;
          fetchChunk(selectedFile, nextChunk, activeFilter, undefined, undefined, scrollColSubset, undefined, true).finally(() => {
            prefetchInProgressRef.current = false;
          });
        }
      } else if (unloadedAbove > 100 && startRow > 0) {
        // Scrolling up - prefetch previous chunk
        const prevChunk = Math.max(0, startChunk - 1);
        if (!cache.has(prevChunk * cs) && !fetchingChunksRef.current.has(prevChunk)) {
          prefetchInProgressRef.current = true;
          fetchChunk(selectedFile, prevChunk, activeFilter, undefined, undefined, scrollColSubset, undefined, true).finally(() => {
            prefetchInProgressRef.current = false;
          });
        }
      }

      // Column windowing: fetch missing columns when user scrolls horizontally
      if (schema.length > 20 && !colFetchInProgressRef.current) {
        const allColNames = schema.map((c) => c.name);
        const neededEndCol = Math.min(endCol + PREFETCH_EXTRA_COLS, allColNames.length);
        const missingCols: string[] = [];
        for (let i = startCol; i < neededEndCol; i++) {
          if (i < allColNames.length && !loadedColumnsRef.current.has(allColNames[i])) {
            missingCols.push(allColNames[i]);
          }
        }

        if (missingCols.length > 0) {
          colFetchInProgressRef.current = true;
          // Mark as loaded to prevent re-fetch
          missingCols.forEach((c) => loadedColumnsRef.current.add(c));

          // Fetch missing columns for loaded row range
          const fetchEnd = Math.min(endRow + PREFETCH_EXTRA_ROWS, maxRows);
          fetchColumnData(missingCols, 0, fetchEnd)
            .finally(() => { colFetchInProgressRef.current = false; });
        }
      }
    }, FETCH_DEBOUNCE_MS);
  }, [selectedFile, activeFilter, totalMatched, totalRows, fetchChunk, schema, fetchColumnData]);

  const handleCellHover = useCallback((row: number, col: number, value: unknown, x: number, y: number) => {
    if (value === null || value === undefined || String(value).trim() === '') {
      setTooltip(null);
      return;
    }
    setTooltip({ row, col, value, x, y });
  }, []);

  const handleCellLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  const handleSearch = () => {
    if (!selectedFile) return;
    if (demoCtx?.active) return; // Skip real API calls during demo


    if (!filterColumn) return;
    const filter: SearchCriteria = {
      column: filterColumn,
      operator: filterOperator as SearchCriteria['operator'],
      value: filterValue,
    };
    stopSearch();
    setActiveFilter(filter);
    setDataCache(new Map());
    fetchingChunksRef.current.clear();
    loadedColumnsRef.current = new Set();
    timerLockedRef.current = false;
    fetchChunk(selectedFile, 0, filter);
  };

  const clearFilter = () => {
    setActiveFilter(null);
    setTotalMatched(0);

    setDataCache(new Map()); // Clear cache
    fetchingChunksRef.current.clear();
    loadedColumnsRef.current = new Set();
    timerLockedRef.current = false;
    stopSearch();
    // Restore pre-search column visibility
    if (preSearchColumnsRef.current) {
      setVisibleColumns(preSearchColumnsRef.current);
      preSearchColumnsRef.current = null;
    }
    if (selectedFile) {
      fetchChunk(selectedFile, 0, null);
    }
  };

  /** Stop search polling */
  const stopSearch = () => {
    if (searchPollRef.current) {
      clearTimeout(searchPollRef.current);
      searchPollRef.current = null;
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (searchPollRef.current) {
        clearTimeout(searchPollRef.current);
      }
      if (liveSearchTimerRef.current) {
        clearTimeout(liveSearchTimerRef.current);
      }
      if (downloadAbortRef.current) {
        downloadAbortRef.current.abort();
      }
      if (dataModeRef.current === 'local') {
        import('../../services/duckdbService').then((m) => m.duckdbService.close());
      }
    };
  }, []);

  const toggleColumn = (columnName: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnName)) {
        next.delete(columnName);
      } else {
        next.add(columnName);
      }
      return next;
    });
  };

  const selectAllColumns = () => {
    setVisibleColumns(new Set(schema.map((c) => c.name)));
  };

  const selectNoColumns = () => {
    setVisibleColumns(new Set());
  };

  const refreshData = () => {
    if (selectedFile) {
      setDataCache(new Map());
      fetchingChunksRef.current.clear();
      timerLockedRef.current = false;
      fetchChunk(selectedFile, 0, activeFilter);
    }
  };

  // Export to CSV
  const exportToCsv = useCallback(() => {
    if (dataCache.size === 0) return;

    const columns = schema.filter((c) => visibleColumns.has(c.name)).map((c) => c.name);
    const rows: string[] = [];

    // Header
    rows.push(columns.map((c) => `"${c}"`).join(','));

    // Data
    const sortedKeys = Array.from(dataCache.keys()).sort((a, b) => a - b);
    for (const key of sortedKeys) {
      const row = dataCache.get(key);
      if (row) {
        const values = columns.map((col) => {
          const val = row[col];
          if (val === null || val === undefined) return '';
          const str = String(val);
          return `"${str.replace(/"/g, '""')}"`;
        });
        rows.push(values.join(','));
      }
    }

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFile?.fileName.replace('.parquet', '') || 'data'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  }, [dataCache, schema, visibleColumns, selectedFile]);

  // Copy to clipboard (tab-separated for Google Sheets)
  const copyToClipboard = useCallback(() => {
    if (dataCache.size === 0) return;

    const columns = schema.filter((c) => visibleColumns.has(c.name)).map((c) => c.name);
    const rows: string[] = [];

    // Header
    rows.push(columns.join('\t'));

    // Data
    const sortedKeys = Array.from(dataCache.keys()).sort((a, b) => a - b);
    for (const key of sortedKeys) {
      const row = dataCache.get(key);
      if (row) {
        const values = columns.map((col) => {
          const val = row[col];
          if (val === null || val === undefined) return '';
          return String(val);
        });
        rows.push(values.join('\t'));
      }
    }

    const tsv = rows.join('\n');
    navigator.clipboard.writeText(tsv);
    toast.success('Copied to clipboard (paste into Google Sheets)');
  }, [dataCache, schema, visibleColumns]);

  // Filter files by search term
  const filteredFiles = files.filter((f) =>
    f.fileName.toLowerCase().includes(fileSearchTerm.toLowerCase())
  );

  // Columns to display (respecting visibility, AI columns placed after their reference)
  const displayColumns = useMemo(() => {
    const base = schema.filter((c) => visibleColumns.has(c.name)).map((c) => c.name);
    const visibleAi = aiColumns.filter((c) => !hiddenAiColumns.has(c.name));
    const placed = new Set<string>();
    const result: string[] = [];
    for (const col of base) {
      result.push(col);
      for (const ai of visibleAi) {
        if (ai.afterColumn === col && !placed.has(ai.name)) {
          result.push(ai.name);
          placed.add(ai.name);
        }
      }
    }
    for (const ai of visibleAi) {
      if (!placed.has(ai.name)) {
        result.push(ai.name);
      }
    }
    return result;
  }, [schema, visibleColumns, aiColumns, hiddenAiColumns]);

  // Set of AI column names for CanvasGrid rendering
  const aiColumnSet = useMemo(() => new Set(aiColumns.map((c) => c.name)), [aiColumns]);

  // Map of AI column name → referenced column name (for single-ref columns)
  const aiColumnRefsMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const ai of aiColumns) {
      if (ai.afterColumn) m.set(ai.name, ai.afterColumn);
    }
    return m;
  }, [aiColumns]);

  // Merged dataCache that overlays AI cell data on top of the real data
  const mergedDataCache = useMemo(() => {
    if (aiColumns.length === 0) return dataCache;
    const merged = new Map(dataCache);
    Array.from(aiCellData.entries()).forEach(([key, value]) => {
      const parts = key.split(':');
      const colName = parts[0];
      const rowIdx = parseInt(parts[1], 10);
      const existing = merged.get(rowIdx);
      if (existing) {
        merged.set(rowIdx, { ...existing, [colName]: value });
      } else {
        merged.set(rowIdx, { [colName]: value });
      }
    });
    // Mark loading cells with hourglass marker
    Array.from(aiCellLoading).forEach((key) => {
      const parts = key.split(':');
      const colName = parts[0];
      const rowIdx = parseInt(parts[1], 10);
      const existing = merged.get(rowIdx);
      if (existing) {
        merged.set(rowIdx, { ...existing, [colName]: '\u23F3' });
      } else {
        merged.set(rowIdx, { [colName]: '\u23F3' });
      }
    });
    return merged;
  }, [dataCache, aiCellData, aiCellLoading, aiColumns]);

  const effectiveRows = activeFilter ? (totalMatched ?? 0) : totalRows;

  // Add or update AI Column
  const handleAddAiColumn = () => {
    const name = newAiColName.trim();
    const prompt = newAiColPrompt.trim();
    if (!name || !prompt) {
      toast.error('Column name and prompt are required');
      return;
    }

    // Detect single column reference for placement
    const refs = prompt.match(/\[([^\]]+)\]/g);
    const uniqueRefs = refs ? Array.from(new Set(refs.map((r) => r.slice(1, -1)))) : [];
    const afterColumn = uniqueRefs.length === 1 && schema.some((c) => c.name === uniqueRefs[0])
      ? uniqueRefs[0]
      : undefined;

    if (editingAiColumn) {
      // Editing existing column
      setAiColumns((prev) =>
        prev.map((c) => c.name === editingAiColumn ? { name, prompt, mode: newAiColMode, model: newAiColModel, afterColumn } : c)
      );
      // If name changed, migrate cell data keys
      if (editingAiColumn !== name) {
        setAiCellData((prev) => {
          const next = new Map<string, string>();
          Array.from(prev.entries()).forEach(([key, value]) => {
            if (key.startsWith(editingAiColumn + ':')) {
              next.set(name + key.slice(editingAiColumn.length), value);
            } else {
              next.set(key, value);
            }
          });
          return next;
        });
      }
      setEditingAiColumn(null);
      setNewAiColName('');
      setNewAiColPrompt('');
      setNewAiColMode('ai');
      setNewAiColModel('amazon.nova-micro-v1:0');
      setShowAiColumnDialog(false);
      toast.success(`AI column "${name}" updated`);
    } else {
      // Adding new column
      if (aiColumns.some((c) => c.name === name) || schema.some((c) => c.name === name)) {
        toast.error('Column name already exists');
        return;
      }
      setAiColumns((prev) => [...prev, { name, prompt, mode: newAiColMode, model: newAiColModel, afterColumn }]);
      setNewAiColName('');
      setNewAiColPrompt('');
      setNewAiColMode('ai');
      setNewAiColModel('amazon.nova-micro-v1:0');
      setShowAiColumnDialog(false);
      toast.success(`AI column "${name}" added — click cells to generate`);
    }
  };

  // Open edit dialog for an existing AI column
  const handleOpenEditAiColumn = (colName: string) => {
    const col = aiColumns.find((c) => c.name === colName);
    if (!col) return;
    setEditingAiColumn(colName);
    setNewAiColName(col.name);
    setNewAiColPrompt(col.prompt);
    setNewAiColMode(col.mode || 'ai');
    setNewAiColModel(col.model || 'amazon.nova-micro-v1:0');
    setShowAiColumnDialog(true);
  };

  // Remove AI Column
  const handleRemoveAiColumn = (colName: string) => {
    setAiColumns((prev) => prev.filter((c) => c.name !== colName));
    setAiCellData((prev) => {
      const next = new Map(prev);
      Array.from(next.keys()).forEach((key) => {
        if (key.startsWith(colName + ':')) next.delete(key);
      });
      return next;
    });
    setAiCellLoading((prev) => {
      const next = new Set(prev);
      Array.from(next).forEach((key) => {
        if (key.startsWith(colName + ':')) next.delete(key);
      });
      return next;
    });
  };

  // Handle prompt textarea changes — detect bracket autocomplete context
  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setNewAiColPrompt(val);
    const cursor = e.target.selectionStart;
    // Look backwards from cursor for an unclosed '['
    const before = val.slice(0, cursor);
    const lastOpen = before.lastIndexOf('[');
    const lastClose = before.lastIndexOf(']');
    if (lastOpen > lastClose) {
      const partial = before.slice(lastOpen + 1).toLowerCase();
      const matches = schema
        .map((c) => c.name)
        .filter((n) => n.toLowerCase().includes(partial));
      setPromptSuggestions(matches);
      setPromptSuggestionIdx(0);
      setPromptBracketStart(lastOpen);
    } else {
      setPromptSuggestions([]);
      setPromptBracketStart(null);
    }
  }, [schema]);

  // Accept a suggestion from the autocomplete list
  const acceptPromptSuggestion = useCallback((fieldName: string) => {
    if (promptBracketStart === null) return;
    const textarea = promptTextareaRef.current;
    const cursor = textarea?.selectionStart ?? newAiColPrompt.length;
    const before = newAiColPrompt.slice(0, promptBracketStart);
    const after = newAiColPrompt.slice(cursor);
    const updated = before + '[' + fieldName + ']' + after;
    setNewAiColPrompt(updated);
    setPromptSuggestions([]);
    setPromptBracketStart(null);
    // Restore focus and cursor position after the inserted reference
    requestAnimationFrame(() => {
      if (textarea) {
        const pos = before.length + fieldName.length + 2;
        textarea.focus();
        textarea.setSelectionRange(pos, pos);
      }
    });
  }, [promptBracketStart, newAiColPrompt]);

  // Handle keyboard navigation in suggestion list
  const handlePromptKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (promptSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPromptSuggestionIdx((prev) => Math.min(prev + 1, promptSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPromptSuggestionIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      acceptPromptSuggestion(promptSuggestions[promptSuggestionIdx]);
    } else if (e.key === 'Escape') {
      setPromptSuggestions([]);
      setPromptBracketStart(null);
    }
  }, [promptSuggestions, promptSuggestionIdx, acceptPromptSuggestion]);

  // Resolve [column] tags in an AI prompt with actual row values
  const resolvePrompt = (prompt: string, rowData: Record<string, unknown>): string => {
    return prompt.replace(/\[([^\]]+)\]/g, (match, colName) => {
      const val = rowData[colName];
      if (val !== null && val !== undefined) return String(val);
      return match; // Leave unresolved if column not found
    });
  };

  // Extract column names referenced in prompt via [ColumnName] tags
  const extractReferencedColumns = (prompt: string): string[] => {
    const refs: string[] = [];
    const regex = /\[([^\]]+)\]/g;
    let m;
    while ((m = regex.exec(prompt)) !== null) refs.push(m[1]);
    return refs;
  };

  // Build row_data containing ONLY the columns referenced in the prompt
  const buildContextRow = (prompt: string, rawRow: Record<string, unknown>): Record<string, unknown> => {
    const refs = extractReferencedColumns(prompt);
    const contextRow: Record<string, unknown> = {};
    for (const col of refs) {
      if (col in rawRow) {
        const v = rawRow[col];
        contextRow[col] = typeof v === 'string' && v.length > 500 ? v.slice(0, 500) : v;
      }
    }
    return contextRow;
  };

  // Check if any referenced column value in the row is blank/NA/empty
  const hasBlankReferencedColumn = (prompt: string, rawRow: Record<string, unknown>): boolean => {
    const refs = extractReferencedColumns(prompt);
    for (const col of refs) {
      const v = rawRow[col];
      if (v === null || v === undefined) return true;
      const s = String(v).trim().toLowerCase();
      if (s === '' || s === 'na' || s === 'n/a' || s === 'null' || s === 'none') return true;
    }
    return false;
  };

  // Handle cell click — trigger AI inference for AI column cells
  const handleCellClick = useCallback((row: number, col: number) => {
    const colName = displayColumns[col];
    if (!colName) return;
    const aiCol = aiColumns.find((c) => c.name === colName);
    if (!aiCol) return; // Not an AI column, ignore

    const cellKey = `${colName}:${row}`;

    // Skip if already loading
    if (aiCellLoading.has(cellKey)) return;

    // Get row data for context
    const rowData = dataCache.get(row);
    if (!rowData) {
      toast.error('Row data not loaded yet');
      return;
    }

    const rawRow = rowData as Record<string, unknown>;

    // Skip if originating column (afterColumn) is blank/NA
    if (aiCol.afterColumn) {
      const refVal = rawRow[aiCol.afterColumn];
      const s = refVal === null || refVal === undefined ? '' : String(refVal).trim().toLowerCase();
      if (s === '' || s === 'na' || s === 'n/a' || s === 'null' || s === 'none') {
        toast.error(`Originating column "${aiCol.afterColumn}" is blank for this row`);
        return;
      }
    }

    // Mark as loading
    setAiCellLoading((prev) => new Set(prev).add(cellKey));

    // Build context with ONLY the columns mentioned in the prompt
    const contextRow = buildContextRow(aiCol.prompt, rawRow);
    const resolved = resolvePrompt(aiCol.prompt, rawRow);
    const modelId = aiCol.model || 'amazon.nova-micro-v1:0';
    lambdaService.inferAiCell(resolved, contextRow, colName, modelId, aiCol.mode || 'ai')
      .then((result) => {
        setAiCellLoading((prev) => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
        if (result.success) {
          setAiCellData((prev) => new Map(prev).set(cellKey, result.value || '—'));
        } else {
          setAiCellData((prev) => new Map(prev).set(cellKey, 'Error'));
          toast.error(result.error || 'Inference failed');
        }
      })
      .catch((err) => {
        setAiCellLoading((prev) => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
        setAiCellData((prev) => new Map(prev).set(cellKey, 'Error'));
        toast.error(`Inference failed: ${err?.message || 'Network error'}`);
      });
  }, [displayColumns, aiColumns, aiCellLoading, dataCache]);

  // Fill all visible AI cells for a column
  const handleFillVisibleCells = useCallback((colName: string) => {
    const aiCol = aiColumns.find((c) => c.name === colName);
    if (!aiCol) return;

    const sortedRows = Array.from(dataCache.keys()).sort((a, b) => a - b).slice(0, 50);
    let queued = 0;

    let skipped = 0;
    for (const row of sortedRows) {
      const cellKey = `${colName}:${row}`;
      if (aiCellLoading.has(cellKey) || aiCellData.has(cellKey)) continue;

      const rowData = dataCache.get(row);
      if (!rowData) continue;

      const rawRow = rowData as Record<string, unknown>;

      // Skip rows where the afterColumn (originating column) is blank/NA
      if (aiCol.afterColumn) {
        const refVal = rawRow[aiCol.afterColumn];
        if (refVal === null || refVal === undefined) { skipped++; continue; }
        const s = String(refVal).trim().toLowerCase();
        if (s === '' || s === 'na' || s === 'n/a' || s === 'null' || s === 'none') { skipped++; continue; }
      }

      queued++;
      setAiCellLoading((prev) => new Set(prev).add(cellKey));

      // Build context with ONLY the columns mentioned in the prompt
      const contextRow = buildContextRow(aiCol.prompt, rawRow);
      const resolved = resolvePrompt(aiCol.prompt, rawRow);
      const batchModelId = aiCol.model || 'amazon.nova-micro-v1:0';
      lambdaService.inferAiCell(resolved, contextRow, colName, batchModelId, aiCol.mode || 'ai')
        .then((result) => {
          setAiCellLoading((prev) => {
            const next = new Set(prev);
            next.delete(cellKey);
            return next;
          });
          if (result.success) {
            setAiCellData((prev) => new Map(prev).set(cellKey, result.value || '—'));
          } else {
            setAiCellData((prev) => new Map(prev).set(cellKey, 'Error'));
          }
        })
        .catch(() => {
          setAiCellLoading((prev) => {
            const next = new Set(prev);
            next.delete(cellKey);
            return next;
          });
          setAiCellData((prev) => new Map(prev).set(cellKey, 'Error'));
        });
    }

    if (queued > 0) {
      const skipMsg = skipped > 0 ? ` (${skipped} blank rows skipped)` : '';
      toast(`Filling ${queued} cells in "${colName}"...${skipMsg}`);
    } else if (skipped > 0) {
      toast(`All ${skipped} rows had blank values — nothing to fill`);
    }
  }, [aiColumns, dataCache, aiCellLoading, aiCellData]);

  // Save current AI columns + cell data as a layer
  const handleSaveLayer = useCallback(() => {
    if (!selectedFile || aiColumns.length === 0) return;
    const name = saveLayerName.trim();
    if (!name) {
      toast.error('Layer name is required');
      return;
    }

    // Convert Map to plain object for JSON serialization
    const cellDataObj: Record<string, string> = {};
    Array.from(aiCellData.entries()).forEach(([key, value]) => {
      cellDataObj[key] = value;
    });

    const layer = {
      name,
      savedAt: new Date().toISOString(),
      columns: aiColumns,
      cellData: cellDataObj,
    };

    const updated = [...savedLayers];
    const idx = updated.findIndex((l) => l.name === name);
    if (idx >= 0) {
      updated[idx] = layer;
    } else {
      updated.push(layer);
    }

    // Save to S3
    const { bucket, key } = getS3Location(selectedFile);
    sheetsService.saveLayers(bucket, key, updated).catch(() => toast.error('Failed to save layer to S3'));
    setSavedLayers(updated);
    setSaveLayerName('');
    setShowSaveLayerDialog(false);
    setActiveLayerName(name);
    toast.success(`Layer "${name}" saved (${aiColumns.length} columns, ${Object.keys(cellDataObj).length} cells)`);
  }, [selectedFile, aiColumns, aiCellData, saveLayerName, savedLayers]);

  // Save over an existing layer without opening the dialog
  const handleSaveOverLayer = useCallback((layerName: string) => {
    if (!selectedFile || aiColumns.length === 0) return;

    const cellDataObj: Record<string, string> = {};
    Array.from(aiCellData.entries()).forEach(([key, value]) => {
      cellDataObj[key] = value;
    });

    const layer = {
      name: layerName,
      savedAt: new Date().toISOString(),
      columns: aiColumns,
      cellData: cellDataObj,
    };

    const updated = [...savedLayers];
    const idx = updated.findIndex((l) => l.name === layerName);
    if (idx >= 0) {
      updated[idx] = layer;
    } else {
      updated.push(layer);
    }

    const { bucket, key } = getS3Location(selectedFile);
    sheetsService.saveLayers(bucket, key, updated).catch(() => toast.error('Failed to save layer to S3'));
    setSavedLayers(updated);
    setActiveLayerName(layerName);
    toast.success(`Layer "${layerName}" updated`);
  }, [selectedFile, aiColumns, aiCellData, savedLayers]);

  // Load a layer — restores AI columns and cell data
  const handleLoadLayer = useCallback((layer: {
    name: string;
    columns: Array<{ name: string; prompt: string; mode?: 'ai' | 'web_search'; model?: string; afterColumn?: string }>;
    cellData: Record<string, string>;
  }) => {
    setAiColumns(layer.columns.map((c) => ({ ...c, mode: c.mode || 'ai', model: c.model, afterColumn: c.afterColumn })));
    const cellMap = new Map<string, string>();
    Object.entries(layer.cellData).forEach(([key, value]) => {
      cellMap.set(key, value);
    });
    setAiCellData(cellMap);
    setAiCellLoading(new Set());
    setActiveLayerName(layer.name);
    toast.success(`Layer "${layer.name}" loaded (${layer.columns.length} columns, ${Object.keys(layer.cellData).length} cells)`);
  }, []);

  // Delete a saved layer
  const handleDeleteLayer = useCallback((layerName: string) => {
    if (!selectedFile) return;
    const filtered = savedLayers.filter((l) => l.name !== layerName);
    const { bucket, key } = getS3Location(selectedFile);
    sheetsService.saveLayers(bucket, key, filtered).catch(() => toast.error('Failed to delete layer from S3'));
    setSavedLayers(filtered);
    if (activeLayerName === layerName) setActiveLayerName(null);
    toast.success(`Layer "${layerName}" deleted`);
  }, [selectedFile, savedLayers, activeLayerName]);

  return (
    <div className="h-[100dvh] flex flex-col bg-background">
      <TopNav />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar - File Selection */}
        {!sidebarCollapsed && (
        <div data-demo="file-sidebar" className="w-72 border-r bg-card p-4 flex flex-col">
          <div className="mb-4">
            <h2 className="font-semibold text-base flex items-center gap-2 mb-3">
              <FileSpreadsheet className="h-5 w-5" />
              <span className="flex-1">Data Files</span>
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Hide file list"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </h2>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                value={fileSearchTerm}
                onChange={(e) => setFileSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto space-y-1">
            {isLoadingFiles ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredFiles.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No files found
              </p>
            ) : (
              filteredFiles.map((file) => {
                const fileKey = file.s3Key || file.fileName;
                const folderName = file.fileName;
                const displayName = folderName.replace(/^\d+-/, '');
                const sizeMB = file.fileSize / 1024 / 1024;
                const sizeStr = sizeMB >= 1024
                  ? `${(sizeMB / 1024).toFixed(2)} GB`
                  : sizeMB > 0
                    ? `${sizeMB.toFixed(1)} MB`
                    : '';
                const isLargeFile = file.isMultipart;
                const children = folderChildren.get(folderName);
                const hasChildren = children && children.length >= 1;
                const isExpanded = expandedFolders.has(folderName);
                const isSelected = !hasChildren && (selectedFile?.s3Key || selectedFile?.fileName) === fileKey;

                return (
                  <div key={fileKey} data-demo={`file-${displayName}`}>
                    <button
                      onClick={() => {
                        if (hasChildren) {
                          setExpandedFolders((prev) => {
                            const next = new Set(prev);
                            if (next.has(folderName)) next.delete(folderName);
                            else next.add(folderName);
                            return next;
                          });
                        } else {
                          handleFileSelect(file);
                        }
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <div className="truncate font-medium flex items-center gap-1">
                        {hasChildren && (
                          <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                        )}
                        {displayName}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {hasChildren && (
                          <span className="text-xs opacity-60">{children.length} {children.length === 1 ? 'file' : 'files'}</span>
                        )}
                        {isLargeFile && (
                          <span className="text-xs opacity-60 inline-flex items-center gap-0.5">
                            <Database className="h-3 w-3" />
                            Large File
                          </span>
                        )}
                        {!hasChildren && sizeStr && (
                          <span className="text-xs opacity-70">{sizeStr}</span>
                        )}
                        {file.fileType === 'audio' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">
                            <Mic className="h-3 w-3" />Audio
                          </span>
                        )}
                        {file.fileType === 'pdf' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-orange-600 dark:text-orange-400">
                            <FileType2 className="h-3 w-3" />PDF
                          </span>
                        )}
                        {file.fileType === 'video' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-purple-600 dark:text-purple-400">
                            <Video className="h-3 w-3" />Video
                          </span>
                        )}
                        {file.fileType === 'image' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-teal-600 dark:text-teal-400">
                            <Image className="h-3 w-3" />Image
                          </span>
                        )}
                      </div>
                    </button>
                    {hasChildren && isExpanded && (
                      <div className="ml-4 space-y-0.5 mt-0.5">
                        {children.map((child) => {
                          const childKey = child.s3Key || child.fileName;
                          const childSelected = (selectedFile?.s3Key || selectedFile?.fileName) === childKey;
                          const childName = childKey.split('/').pop()?.replace('.parquet', '').replace(/^\d+-/, '') || child.fileName;
                          const childSizeBytes = child.fileSize || 0;
                          const childSizeMB = childSizeBytes / 1024 / 1024;
                          const childSizeStr = childSizeMB >= 1024
                            ? `${(childSizeMB / 1024).toFixed(2)} GB`
                            : childSizeMB >= 1
                              ? `${childSizeMB.toFixed(1)} MB`
                              : childSizeBytes > 0
                                ? `${(childSizeBytes / 1024).toFixed(0)} KB`
                                : '';
                          return (
                            <button
                              key={childKey}
                              onClick={() => handleFileSelect(child)}
                              className={`w-full text-left px-3 py-1.5 rounded-md text-xs transition-colors ${
                                childSelected
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-muted'
                              }`}
                            >
                              <div className="truncate">{childName}</div>
                              {childSizeStr && <div className="opacity-70">{childSizeStr}</div>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div data-demo="search-toolbar" className="px-3 py-2 flex items-center gap-3 flex-nowrap overflow-x-auto border-b border-border/50">
            {sidebarCollapsed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarCollapsed(false)}
                title="Show file list"
                className="px-2"
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            )}
            {/* Search Controls */}
            <div className="flex items-center gap-2">
              <Select value={filterColumn} onValueChange={setFilterColumn}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Column" />
                </SelectTrigger>
                <SelectContent>
                  {schema.map((col) => (
                    <SelectItem key={col.name} value={col.name}>
                      {col.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterOperator} onValueChange={setFilterOperator}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(OPERATORS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                data-demo="search-input"
                ref={searchInputRef}
                placeholder="Value..."
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-40"
              />

              {/* Search button */}
              <Button onClick={handleSearch} size="sm" disabled={!selectedFile || isLoading}>
                <Search className="h-4 w-4 mr-1" />
                Search
              </Button>

              {activeFilter && (
                <Button variant="ghost" size="sm" onClick={clearFilter}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="h-6 w-px bg-border" />

            {/* Column Selector */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns className="h-4 w-4 mr-1" />
                  Columns ({visibleColumns.size + aiColumns.length - hiddenAiColumns.size}/{schema.length + aiColumns.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-h-[28rem] flex flex-col p-0" align="start">
                {/* Search — fixed */}
                <div className="bg-popover border-b px-3 pt-3 pb-2 space-y-2 shrink-0">
                  <Input
                    placeholder="Search columns..."
                    value={columnSearchQuery}
                    onChange={(e) => setColumnSearchQuery(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>

                <div className="flex-1 overflow-y-auto">
                  {/* AI Columns section */}
                  {aiColumns.length > 0 && aiColumns.some((col) => !columnSearchQuery || col.name.toLowerCase().includes(columnSearchQuery.toLowerCase())) && (
                    <div className="border-b">
                      <div className="px-3 pt-2 pb-1">
                        <span className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">AI Columns</span>
                      </div>
                      <div className="px-3 pb-2 space-y-1.5">
                        {aiColumns
                          .filter((col) => !columnSearchQuery || col.name.toLowerCase().includes(columnSearchQuery.toLowerCase()))
                          .map((col) => (
                          <div key={`ai-${col.name}`} className="flex items-center space-x-2">
                            <Checkbox
                              id={`col-ai-${col.name}`}
                              checked={!hiddenAiColumns.has(col.name)}
                              onCheckedChange={() => {
                                setHiddenAiColumns((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(col.name)) next.delete(col.name);
                                  else next.add(col.name);
                                  return next;
                                });
                              }}
                            />
                            <Label
                              htmlFor={`col-ai-${col.name}`}
                              className="text-sm flex-1 cursor-pointer"
                            >
                              <span className="text-blue-500">{col.name}</span>
                              <span className="text-xs text-muted-foreground ml-1.5">
                                ({col.mode === 'web_search' ? 'web' : 'ai'})
                              </span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Data Columns section */}
                  <div>
                    <div className="px-3 pt-2 pb-1 flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Data Columns</span>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={selectAllColumns}>
                          All
                        </Button>
                        <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={selectNoColumns}>
                          None
                        </Button>
                      </div>
                    </div>
                    <div className="px-3 pb-2 space-y-1.5">
                      {schema
                        .filter((col) =>
                          !columnSearchQuery || col.name.toLowerCase().includes(columnSearchQuery.toLowerCase())
                        )
                        .map((col) => (
                        <div key={col.name} className="flex items-center space-x-2">
                          <Checkbox
                            id={`col-${col.name}`}
                            checked={visibleColumns.has(col.name)}
                            onCheckedChange={() => toggleColumn(col.name)}
                          />
                          <Label
                            htmlFor={`col-${col.name}`}
                            className="text-sm flex-1 cursor-pointer"
                          >
                            {col.name}
                            <span className="text-xs text-muted-foreground ml-2">
                              ({col.data_type})
                            </span>
                          </Label>
                        </div>
                      ))}
                      {columnSearchQuery && schema.filter((col) => col.name.toLowerCase().includes(columnSearchQuery.toLowerCase())).length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-2">No columns match</p>
                      )}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* AI Columns — combined add + manage dropdown */}
            {selectedFile && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    className="gap-1.5 bg-blue-500 hover:bg-blue-600 text-white border-0"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    AI Columns
                    {aiColumns.length > 0 && (
                      <span className="text-[10px] bg-blue-400/50 rounded-full px-1.5">{aiColumns.length}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem
                    onSelect={() => {
                      setEditingAiColumn(null);
                      setNewAiColName('');
                      setNewAiColPrompt('');
                      setShowAiColumnDialog(true);
                    }}
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-2" />
                    <span className="text-sm">Add AI Column</span>
                  </DropdownMenuItem>
                  {aiColumns.length > 0 && <div className="h-px bg-border my-1" />}
                  {aiColumns.map((col) => (
                    <DropdownMenuItem
                      key={col.name}
                      className="flex items-center justify-between gap-2 pr-1"
                      onSelect={(e) => e.preventDefault()}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-1.5">
                          {col.name}
                          {col.mode === 'web_search' && (
                            <Globe className="h-3 w-3 text-green-500 shrink-0" />
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">{col.prompt}</div>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button
                          className="p-1 rounded hover:bg-muted"
                          onClick={() => handleOpenEditAiColumn(col.name)}
                          title="Edit column"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          className="p-1 rounded hover:bg-muted"
                          onClick={() => handleFillVisibleCells(col.name)}
                          title="Fill visible cells"
                        >
                          <Play className="h-3 w-3" />
                        </button>
                        <button
                          className="p-1 rounded hover:bg-destructive/20 hover:text-destructive"
                          onClick={() => handleRemoveAiColumn(col.name)}
                          title="Remove"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Layers — save/load AI column state */}
            {selectedFile && (aiColumns.length > 0 || savedLayers.length > 0) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Layers className="h-3.5 w-3.5" />
                    Layers
                    {savedLayers.length > 0 && (
                      <span className="text-[10px] bg-muted rounded-full px-1.5">{savedLayers.length}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  {aiColumns.length > 0 && (
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        setSaveLayerName('');
                        setShowSaveLayerDialog(true);
                      }}
                    >
                      <Save className="h-3.5 w-3.5 mr-2" />
                      <span className="text-sm">Save Current Layer</span>
                    </DropdownMenuItem>
                  )}
                  {savedLayers.length > 0 && aiColumns.length > 0 && (
                    <div className="h-px bg-border my-1" />
                  )}
                  {savedLayers.map((layer) => {
                    const cellCount = Object.keys(layer.cellData).length;
                    const savedDate = new Date(layer.savedAt);
                    const dateStr = savedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                    const isActive = activeLayerName === layer.name;
                    return (
                      <DropdownMenuItem
                        key={layer.name}
                        className={`flex items-center justify-between gap-2 pr-1 ${isActive ? 'bg-blue-500/15' : ''}`}
                        onSelect={(e) => e.preventDefault()}
                      >
                        <button
                          className="flex-1 min-w-0 text-left"
                          onClick={() => handleLoadLayer(layer)}
                        >
                          <div className="text-sm font-medium truncate flex items-center gap-1.5">
                            {isActive && <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />}
                            {layer.name}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {layer.columns.length} cols · {cellCount} cells · {dateStr}
                          </div>
                        </button>
                        <div className="flex items-center gap-0.5 shrink-0">
                          {aiColumns.length > 0 && (
                            <button
                              className="p-1 rounded hover:bg-blue-500/20 hover:text-blue-400"
                              onClick={() => handleSaveOverLayer(layer.name)}
                              title="Save over this layer"
                            >
                              <Save className="h-3 w-3" />
                            </button>
                          )}
                          <button
                            className="p-1 rounded hover:bg-destructive/20 hover:text-destructive"
                            onClick={() => handleDeleteLayer(layer.name)}
                            title="Delete layer"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                  {savedLayers.length === 0 && aiColumns.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                      No saved layers
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Pivot Table - Coming Soon */}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="cursor-default opacity-80 relative">
                    <TableProperties className="h-4 w-4 mr-1" />
                    Pivot Table
                    <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-orange-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-64 p-3">
                  <p className="font-semibold mb-1">Request Access</p>
                  <p className="text-xs text-muted-foreground">
                    Pivot Tables let you summarize, group, and aggregate millions of rows instantly. Drag and drop fields to create dynamic cross-tabulations with calculated metrics.
                  </p>
                  <p className="text-xs text-orange-400 mt-1.5">Coming soon — contact us for early access.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Button variant="outline" size="sm" onClick={refreshData} disabled={!selectedFile}>
              <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>

            <div className="flex-1" />

            {/* Stats */}
            {selectedFile && (
              <div data-demo="stats-bar" className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Rows className="h-3.5 w-3.5" />
                  {effectiveRows.toLocaleString()} rows
                </span>
                {lastQueryTime && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {lastQueryTime}ms
                  </span>
                )}
                {cacheHit && (
                  <Badge variant="secondary" className="text-xs">
                    Cached
                  </Badge>
                )}
                {dataMode === 'local' && (
                  <Badge variant="outline" className="text-xs text-green-400 border-green-400/30">
                    <Zap className="h-3 w-3 mr-1" />
                    Local
                  </Badge>
                )}
                {downloadProgress !== null && (
                  <span className="text-xs text-muted-foreground">
                    Downloading... {downloadProgress}%
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Active Search Badge */}
          {activeFilter && (
            <div className="px-3 py-2 bg-muted/50 border-b flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Active search:</span>
              <Badge variant="secondary">
                {activeFilter.column} {OPERATORS[activeFilter.operator]} "{activeFilter.value}"
              </Badge>
              <span className="text-sm text-muted-foreground">
                ({(totalMatched ?? 0).toLocaleString()} matches)
              </span>
            </div>
          )}

          {/* Canvas Grid */}
          <div data-demo="canvas-grid" className="flex-1 relative">
            {!selectedFile ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center">
                  <Sheet className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">Select a file to view data</p>
                  <p className="text-sm">Choose a file from the sidebar</p>
                </div>
              </div>
            ) : isLoading && dataCache.size === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                  <p className="text-muted-foreground">Loading data...</p>
                </div>
              </div>
            ) : (
              <CanvasGrid
                columns={displayColumns}
                totalRows={effectiveRows}
                dataCache={mergedDataCache}
                isLoading={isLoading}
                theme={canvasTheme}
                onScroll={handleScroll}
                onCellHover={handleCellHover}
                onCellLeave={handleCellLeave}
                onCellClick={handleCellClick}
                aiColumns={aiColumnSet}
                aiColumnRefs={aiColumnRefsMap}
                aiLoading={aiCellLoading.size > 0}
              />
            )}

            {/* Cell tooltip - positioned near cursor */}
            {tooltip && (() => {
              const text = tooltip.value === null || tooltip.value === undefined
                ? 'null'
                : String(tooltip.value);
              const colName = displayColumns[tooltip.col] || '';
              const isLong = text.length > 80 || text.includes('\n');
              return (
                <div
                  className="fixed z-50 pointer-events-none"
                  style={{
                    left: Math.min(tooltip.x + 12, window.innerWidth - 420),
                    top: Math.max(8, tooltip.y - (isLong ? 120 : 36)),
                  }}
                >
                  <div className="bg-popover text-popover-foreground rounded-lg shadow-xl border border-border/80 overflow-hidden" style={{ maxWidth: 400 }}>
                    <div className="px-3 py-1.5 bg-muted/50 border-b border-border/50 flex items-center gap-2 text-xs">
                      <span className="font-semibold text-primary">{colName}</span>
                      <span className="text-muted-foreground">Row {tooltip.row + 1}</span>
                    </div>
                    <div className="px-3 py-2 text-sm font-mono whitespace-pre-wrap break-words leading-relaxed" style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {text}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Footer - Status Bar */}
          {selectedFile && (
            <div className="border-t bg-card px-3 py-2 flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                {activeFilter && (
                  <span className="text-amber-500">
                    Filtered
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span>↑↓←→ Navigate</span>
                <span>PgUp/PgDn Scroll</span>
                <span>Ctrl+Home/End Jump</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add AI Column Dialog */}
      <Dialog open={showAiColumnDialog} onOpenChange={(open) => {
        setShowAiColumnDialog(open);
        if (!open) {
          setEditingAiColumn(null);
          setNewAiColName('');
          setNewAiColPrompt('');
          setNewAiColMode('ai');
          setNewAiColModel('amazon.nova-micro-v1:0');
          setAiColRefSearch('');
          setPromptSuggestions([]);
          setPromptBracketStart(null);
        }
      }}>
        <DialogContent className="sm:max-w-6xl p-0 gap-0">
          <div className="flex h-[624px]">
            {/* Left Panel — Existing AI Columns */}
            <div className="w-52 border-r flex flex-col bg-muted/20 shrink-0">
              <div className="px-3 pt-4 pb-2 border-b">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">AI Columns</span>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {aiColumns.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No AI columns yet</p>
                ) : (
                  aiColumns.map((col) => (
                    <div
                      key={col.name}
                      className={`rounded-md border px-2.5 py-2 text-xs ${
                        editingAiColumn === col.name
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-border hover:border-muted-foreground/40'
                      }`}
                    >
                      <div className="font-medium truncate flex items-center gap-1">
                        {col.name}
                        {col.mode === 'web_search' && <Globe className="h-3 w-3 text-green-500 shrink-0" />}
                        {col.model?.startsWith('byol:') && <Server className="h-3 w-3 text-orange-500 shrink-0" />}
                      </div>
                      <div className="text-muted-foreground truncate mt-0.5">{col.prompt}</div>
                      {col.model && col.mode === 'ai' && (
                        <div className="text-[10px] text-muted-foreground/70 truncate mt-0.5">
                          {MODEL_OPTIONS.flatMap(g => g.models).find(m => m.id === col.model)?.name || col.model}
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-1.5">
                        <button
                          className="p-1 rounded hover:bg-muted"
                          onClick={() => handleOpenEditAiColumn(col.name)}
                          title="Edit"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          className="p-1 rounded hover:bg-muted"
                          onClick={() => handleFillVisibleCells(col.name)}
                          title="Fill visible cells"
                        >
                          <Play className="h-3 w-3" />
                        </button>
                        <button
                          className="p-1 rounded hover:bg-destructive/20 hover:text-destructive"
                          onClick={() => handleRemoveAiColumn(col.name)}
                          title="Remove"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Center Panel — Prompt Configuration */}
            <div className="flex-1 flex flex-col min-w-0">
              <DialogHeader className="px-5 pt-4 pb-3 border-b">
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  {editingAiColumn ? 'Edit AI Column' : 'Add AI Column'}
                </DialogTitle>
                <DialogDescription>
                  {editingAiColumn
                    ? 'Update the column name, prompt, or mode.'
                    : 'Click a field on the right to insert it as a [reference] in your prompt.'}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                <div>
                  <Label htmlFor="ai-col-name" className="text-sm font-medium">
                    Column Name
                  </Label>
                  <Input
                    id="ai-col-name"
                    placeholder="e.g. Sentiment, Category, Summary"
                    value={newAiColName}
                    onChange={(e) => setNewAiColName(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="ai-col-prompt" className="text-sm font-medium">
                    Prompt
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-1">
                    Use <code className="bg-muted px-1 rounded text-[11px]">[column_name]</code> to reference row values.
                  </p>
                  <div className="relative">
                    <Textarea
                      id="ai-col-prompt"
                      ref={promptTextareaRef}
                      placeholder="e.g. Classify [feedback] as Positive, Negative, or Neutral"
                      value={newAiColPrompt}
                      onChange={handlePromptChange}
                      onKeyDown={handlePromptKeyDown}
                      onBlur={() => { setTimeout(() => { setPromptSuggestions([]); setPromptBracketStart(null); }, 150); }}
                      rows={5}
                      className="resize-none"
                    />
                    {promptSuggestions.length > 0 && (
                      <div className="absolute z-50 left-0 right-0 mt-1 max-h-40 overflow-y-auto rounded-md border bg-popover shadow-lg">
                        {promptSuggestions.map((name, i) => (
                          <button
                            key={name}
                            type="button"
                            className={`w-full text-left text-xs px-3 py-1.5 transition-colors ${
                              i === promptSuggestionIdx
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-muted'
                            }`}
                            onMouseDown={(e) => { e.preventDefault(); acceptPromptSuggestion(name); }}
                          >
                            {name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Source</Label>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                        newAiColMode === 'ai'
                          ? 'border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                      onClick={() => setNewAiColMode('ai')}
                    >
                      <Sparkles className="h-4 w-4" />
                      AI Inference
                    </button>
                    <button
                      type="button"
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                        newAiColMode === 'web_search'
                          ? 'border-green-500 bg-green-500/10 text-green-600 dark:text-green-400'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                      onClick={() => setNewAiColMode('web_search')}
                    >
                      <Globe className="h-4 w-4" />
                      Web Search
                    </button>
                  </div>

                </div>

                {/* Model Selection — only visible for AI mode */}
                {newAiColMode === 'ai' && (
                  <div>
                    <Label className="text-sm font-medium">Model</Label>
                    <Select value={newAiColModel} onValueChange={setNewAiColModel}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a model" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {MODEL_OPTIONS.map((group) => (
                          <React.Fragment key={group.group}>
                            <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                              {group.group}
                            </div>
                            {group.models.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                <span className="flex items-center gap-2">
                                  {m.tier === 'local' && <Server className="h-3 w-3 text-orange-500 shrink-0" />}
                                  {m.tier === 'premium' && <Sparkles className="h-3 w-3 text-purple-500 shrink-0" />}
                                  {m.tier === 'fast' && <Zap className="h-3 w-3 text-yellow-500 shrink-0" />}
                                  {(m.tier === 'standard' || m.tier === 'default') && <Cpu className="h-3 w-3 text-blue-500 shrink-0" />}
                                  <span>{m.name}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center justify-between mt-1.5">
                      <p className="text-[11px] text-muted-foreground">
                        {newAiColModel.startsWith('byol:')
                          ? 'Routes to your local Ollama instance via Tailscale.'
                          : 'Powered by AWS Bedrock.'}
                      </p>
                      <button
                        type="button"
                        className="text-[11px] text-blue-500 hover:text-blue-400 flex items-center gap-1"
                        onClick={() => setShowBYOLSetup(true)}
                      >
                        <Server className="h-3 w-3" />
                        Use your own LLM
                      </button>
                    </div>
                  </div>
                )}
                {newAiColMode === 'web_search' && (
                  <p className="text-[11px] text-muted-foreground">
                    Uses Perplexity web search to look up information based on row data.
                  </p>
                )}
              </div>
              <div className="px-5 py-3 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAiColumnDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddAiColumn} disabled={!newAiColName.trim() || !newAiColPrompt.trim()}>
                  <Sparkles className="h-4 w-4 mr-1" />
                  {editingAiColumn ? 'Update Column' : 'Add Column'}
                </Button>
              </div>
            </div>

            {/* Right Panel — Data Fields */}
            <div className="w-56 border-l flex flex-col bg-muted/20 shrink-0">
              <div className="px-3 pt-4 pb-2 border-b space-y-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Data Fields</span>
                <Input
                  placeholder="Search fields..."
                  value={aiColRefSearch}
                  onChange={(e) => setAiColRefSearch(e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {schema
                  .filter((c) => !aiColRefSearch || c.name.toLowerCase().includes(aiColRefSearch.toLowerCase()))
                  .map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-primary/10 hover:text-primary transition-colors truncate"
                    onClick={() => setNewAiColPrompt((prev) => prev + `[${c.name}]`)}
                    title={`Insert [${c.name}]`}
                  >
                    <span className="font-medium">{c.name}</span>
                    <span className="text-muted-foreground ml-1.5">({c.data_type})</span>
                  </button>
                ))}
                {aiColRefSearch && schema.filter((c) => c.name.toLowerCase().includes(aiColRefSearch.toLowerCase())).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">No fields match</p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Layer Dialog */}
      <Dialog open={showSaveLayerDialog} onOpenChange={setShowSaveLayerDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Save Layer
            </DialogTitle>
            <DialogDescription>
              Save AI columns and populated cells so you can reload them later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="layer-name" className="text-sm font-medium">
                Layer Name
              </Label>
              <Input
                id="layer-name"
                placeholder="e.g. Sentiment Analysis, Enrichment v2"
                value={saveLayerName}
                onChange={(e) => setSaveLayerName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveLayer()}
                className="mt-1"
              />
            </div>
            <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
              <div>{aiColumns.length} AI {aiColumns.length === 1 ? 'column' : 'columns'}</div>
              <div>{aiCellData.size} populated {aiCellData.size === 1 ? 'cell' : 'cells'}</div>
            </div>
            {savedLayers.some((l) => l.name === saveLayerName.trim()) && (
              <p className="text-xs text-amber-400">A layer with this name exists and will be overwritten.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveLayerDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveLayer} disabled={!saveLayerName.trim()}>
              <Save className="h-4 w-4 mr-1" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bring Your Own LLM Setup Dialog */}
      <Dialog open={showBYOLSetup} onOpenChange={setShowBYOLSetup}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="h-5 w-5 text-orange-500" />
              Bring Your Own LLM
            </DialogTitle>
            <DialogDescription>
              Connect your local Ollama instance to BigSplice via Tailscale.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Setup Instructions */}
            <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Setup Guide</p>

              <div className="space-y-2.5">
                <div className="flex gap-2.5">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-500 text-[10px] font-bold mt-0.5">1</div>
                  <div className="text-xs">
                    <p className="font-medium">Install Ollama</p>
                    <p className="text-muted-foreground mt-0.5">
                      Download from{' '}
                      <a href="https://ollama.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-0.5">
                        ollama.com <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                      {' '}and pull a model:
                    </p>
                    <code className="block mt-1 bg-background rounded px-2 py-1 text-[11px] font-mono">
                      ollama pull qwen3.5:4b
                    </code>
                  </div>
                </div>

                <div className="flex gap-2.5">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-500 text-[10px] font-bold mt-0.5">2</div>
                  <div className="text-xs">
                    <p className="font-medium">Install Tailscale</p>
                    <p className="text-muted-foreground mt-0.5">
                      Download from{' '}
                      <a href="https://tailscale.com/download" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-0.5">
                        tailscale.com <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                      {' '}and sign in.
                    </p>
                  </div>
                </div>

                <div className="flex gap-2.5">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-500 text-[10px] font-bold mt-0.5">3</div>
                  <div className="text-xs">
                    <p className="font-medium">Get your Tailscale IP</p>
                    <p className="text-muted-foreground mt-0.5">Run on your machine:</p>
                    <code className="block mt-1 bg-background rounded px-2 py-1 text-[11px] font-mono">
                      tailscale ip -4
                    </code>
                    <p className="text-muted-foreground mt-1">
                      This returns something like <code className="bg-background px-1 rounded text-[10px]">100.x.x.x</code>
                    </p>
                  </div>
                </div>

                <div className="flex gap-2.5">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-500 text-[10px] font-bold mt-0.5">4</div>
                  <div className="text-xs">
                    <p className="font-medium">Generate an auth key</p>
                    <p className="text-muted-foreground mt-0.5">
                      Go to{' '}
                      <a href="https://login.tailscale.com/admin/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-0.5">
                        Tailscale Admin &rarr; Settings &rarr; Keys <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    </p>
                    <p className="text-muted-foreground mt-0.5">
                      Create a key with <strong>Reusable</strong> and <strong>Ephemeral</strong> enabled.
                    </p>
                  </div>
                </div>

                <div className="flex gap-2.5">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-500 text-[10px] font-bold mt-0.5">5</div>
                  <div className="text-xs">
                    <p className="font-medium">Ensure Ollama is running</p>
                    <p className="text-muted-foreground mt-0.5">Ollama serves on port <code className="bg-background px-1 rounded text-[10px]">11434</code> by default.</p>
                    <code className="block mt-1 bg-background rounded px-2 py-1 text-[11px] font-mono">
                      curl http://localhost:11434/api/tags
                    </code>
                  </div>
                </div>
              </div>
            </div>

            {/* Connection Fields */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="byol-ts-key" className="text-sm font-medium flex items-center gap-1.5">
                  <Terminal className="h-3.5 w-3.5" />
                  Tailscale Auth Key
                </Label>
                <Input
                  id="byol-ts-key"
                  type="password"
                  placeholder="tskey-auth-..."
                  value={byolTailscaleKey}
                  onChange={(e) => setByolTailscaleKey(e.target.value)}
                  className="mt-1 font-mono text-xs"
                />
              </div>

              <div>
                <Label htmlFor="byol-machine" className="text-sm font-medium flex items-center gap-1.5">
                  <Server className="h-3.5 w-3.5" />
                  Machine Address
                </Label>
                <Input
                  id="byol-machine"
                  placeholder="100.x.x.x:11434"
                  value={byolMachineAddr}
                  onChange={(e) => setByolMachineAddr(e.target.value)}
                  className="mt-1 font-mono text-xs"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Tailscale IP + Ollama port (default: 11434)
                </p>
              </div>
            </div>

            {/* Test Connection */}
            {byolTestStatus !== 'idle' && (
              <div className={`rounded-md border p-2.5 flex items-center gap-2 text-xs ${
                byolTestStatus === 'success'
                  ? 'border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400'
                  : byolTestStatus === 'error'
                  ? 'border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400'
                  : 'border-blue-500/50 bg-blue-500/10 text-blue-600 dark:text-blue-400'
              }`}>
                {byolTestStatus === 'testing' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {byolTestStatus === 'success' && <CheckCircle className="h-3.5 w-3.5" />}
                {byolTestStatus === 'error' && <AlertCircle className="h-3.5 w-3.5" />}
                <span>{byolTestMessage}</span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              disabled={!byolTailscaleKey || !byolMachineAddr || byolTestStatus === 'testing'}
              onClick={async () => {
                setByolTestStatus('testing');
                setByolTestMessage('Connecting via Tailscale...');
                try {
                  const resp = await fetch(
                    'https://3bgxduhphyaihvueuiolms2s4a0kubeo.lambda-url.us-east-1.on.aws/health'
                  );
                  if (resp.ok) {
                    setByolTestStatus('success');
                    setByolTestMessage('Gateway reachable. Your LLM connection is ready.');
                  } else {
                    setByolTestStatus('error');
                    setByolTestMessage(`Gateway returned ${resp.status}`);
                  }
                } catch {
                  setByolTestStatus('error');
                  setByolTestMessage('Cannot reach gateway. Check your network.');
                }
              }}
            >
              {byolTestStatus === 'testing' ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <HelpCircle className="h-4 w-4 mr-1" />
              )}
              Test Connection
            </Button>
            <Button
              onClick={async () => {
                setByolSaving(true);
                try {
                  const headers = await lambdaService.getAuthHeaders();
                  const resp = await fetch(
                    lambdaEndpoints.aiColumnInference.replace('/ai-column', '/ai-column/byol-config'),
                    {
                      method: 'POST',
                      headers,
                      body: JSON.stringify({
                        tailscale_key: byolTailscaleKey,
                        machine_addr: byolMachineAddr,
                      }),
                    }
                  );
                  const data = await resp.json();
                  const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
                  if (parsed.success) {
                    setByolConfigured(true);
                    setNewAiColModel('byol:default');
                    setShowBYOLSetup(false);
                    toast.success('Local LLM saved — select "Your LLM (Ollama)" in the model dropdown');
                  } else {
                    toast.error(parsed.error || 'Failed to save BYOL config');
                  }
                } catch (err: any) {
                  toast.error(`Save failed: ${err?.message || 'Network error'}`);
                } finally {
                  setByolSaving(false);
                }
              }}
              disabled={!byolTailscaleKey || !byolMachineAddr || byolSaving}
            >
              {byolSaving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Save Connection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SheetsPage;
